let isMacPlatformCache: boolean | undefined;

/**
 * Detects if the current platform is macOS using multiple detection methods
 * for maximum compatibility across different browsers and versions.
 *
 * @returns {boolean} True if the current platform is macOS, false otherwise
 *
 * @remarks
 * This function is memoized since the platform detection result won't change
 * during the application's lifetime, improving performance for repeated calls.
 */
export function isMacPlatform(): boolean {
  // Return cached result if available
  if (isMacPlatformCache !== undefined) {
    return isMacPlatformCache;
  }

  // Method 1: Check modern userAgentData API (most reliable when available)
  if (typeof navigator !== "undefined" && "userAgentData" in navigator) {
    const userAgentData = (navigator as any).userAgentData;
    if (userAgentData && userAgentData.platform) {
      isMacPlatformCache = userAgentData.platform.toLowerCase() === "macos";
      return isMacPlatformCache;
    }
  }

  // Method 2: Check navigator.platform (deprecated but still widely supported)
  if (typeof navigator !== "undefined") {
    const platformValue = navigator.platform;
    if (platformValue) {
      const platform = platformValue.toLowerCase();
      if (platform.includes("mac") || platform.includes("darwin")) {
        isMacPlatformCache = true;
        return isMacPlatformCache;
      }
    }
  }

  // Method 3: Check user agent string as fallback
  if (typeof navigator !== "undefined") {
    const userAgentValue = navigator.userAgent;
    if (userAgentValue) {
      const userAgent = userAgentValue.toLowerCase();
      if (userAgent.includes("mac os x") || userAgent.includes("macintosh")) {
        isMacPlatformCache = true;
        return isMacPlatformCache;
      }
    }
  }

  // Final fallback: assume non-Mac if all detection methods fail
  isMacPlatformCache = false;
  return isMacPlatformCache;
}

// Cache for memoization - platform detection result won't change during runtime
let isWindowsPlatformCache: boolean | undefined;

/**
 * Detects if the current platform is Windows
 *
 * @returns {boolean} True if the current platform is Windows, false otherwise
 *
 * @remarks
 * This function is memoized since the platform detection result won't change
 * during the application's lifetime, improving performance for repeated calls.
 */
export function isWindowsPlatform(): boolean {
  // Return cached result if available
  if (isWindowsPlatformCache !== undefined) {
    return isWindowsPlatformCache;
  }

  if (typeof navigator !== "undefined" && "userAgentData" in navigator) {
    const userAgentData = (navigator as any).userAgentData;
    if (userAgentData && userAgentData.platform) {
      isWindowsPlatformCache = userAgentData.platform.toLowerCase() === "windows";
      return isWindowsPlatformCache;
    }
  }

  if (typeof navigator !== "undefined") {
    const platformValue = navigator.platform;
    if (platformValue) {
      const platform = platformValue.toLowerCase();
      if (platform.includes("win")) {
        isWindowsPlatformCache = true;
        return isWindowsPlatformCache;
      }
    }
  }

  if (typeof navigator !== "undefined") {
    const userAgentValue = navigator.userAgent;
    if (userAgentValue) {
      const userAgent = userAgentValue.toLowerCase();
      isWindowsPlatformCache = userAgent.includes("windows");
      return isWindowsPlatformCache;
    }
  }

  isWindowsPlatformCache = false;
  return isWindowsPlatformCache;
}

// Cache for memoization - platform detection result won't change during runtime
let isLinuxPlatformCache: boolean | undefined;

/**
 * Detects if the current platform is Linux
 *
 * @returns {boolean} True if the current platform is Linux, false otherwise
 *
 * @remarks
 * This function is memoized since the platform detection result won't change
 * during the application's lifetime, improving performance for repeated calls.
 */
export function isLinuxPlatform(): boolean {
  // Return cached result if available
  if (isLinuxPlatformCache !== undefined) {
    return isLinuxPlatformCache;
  }

  if (typeof navigator !== "undefined" && "userAgentData" in navigator) {
    const userAgentData = (navigator as any).userAgentData;
    if (userAgentData && userAgentData.platform) {
      isLinuxPlatformCache = userAgentData.platform.toLowerCase() === "linux";
      return isLinuxPlatformCache;
    }
  }

  if (typeof navigator !== "undefined") {
    const platformValue = navigator.platform;
    if (platformValue) {
      const platform = platformValue.toLowerCase();
      if (platform.includes("linux")) {
        isLinuxPlatformCache = true;
        return isLinuxPlatformCache;
      }
    }
  }

  if (typeof navigator !== "undefined") {
    const userAgentValue = navigator.userAgent;
    if (userAgentValue) {
      const userAgent = userAgentValue.toLowerCase();
      isLinuxPlatformCache = userAgent.includes("linux");
      return isLinuxPlatformCache;
    }
  }

  isLinuxPlatformCache = false;
  return isLinuxPlatformCache;
}

/**
 * Gets the platform-specific keyboard shortcut format
 */
export function getPlatformShortcut(key: string): string {
  const isMac = isMacPlatform();
  return isMac ? `⌘${key}` : `Ctrl+${key}`;
}

/**
 * Gets the platform-specific modifier key name
 */
export function getPlatformModifierKey(): string {
  return isMacPlatform() ? "⌘" : "Ctrl";
}

/**
 * Resets the platform detection cache.
 * This is primarily intended for testing purposes.
 *
 * @internal
 */
export function resetPlatformCache(): void {
  isMacPlatformCache = undefined;
  isWindowsPlatformCache = undefined;
  isLinuxPlatformCache = undefined;
}

/**
 * Platform detection utilities
 */
export const platform = {
  isMac: isMacPlatform,
  isWindows: isWindowsPlatform,
  isLinux: isLinuxPlatform,
  getShortcut: getPlatformShortcut,
  getModifierKey: getPlatformModifierKey,
} as const;
